import { useEffect, useRef, useState } from "react";
import { BookOpen, CheckCircle2, ChevronDown, CircleHelp, Clock3, LockKeyhole, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const machineKey = (prefix) =>
  `${prefix}:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

const parseApiUtcDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw}Z`;
  const parsed = new Date(zoned);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatNextWorkTime = (value) => {
  const parsed = parseApiUtcDate(value);
  if (!parsed) return null;
  const date = new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "long",
    timeZone: "Europe/Istanbul",
  }).format(parsed);
  const time = new Intl.DateTimeFormat("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "Europe/Istanbul",
  }).format(parsed).replace(":", ".");
  return `${date} saat ${time}`;
};

export const nextWorkSentence = (value) => {
  const transition = formatNextWorkTime(value);
  return transition
    ? `Sıradaki çalışmanız ${transition}’de açılacak.`
    : "Sıradaki çalışmanız zamanı geldiğinde otomatik açılacak.";
};

const remainingWorkTime = (value, now) => {
  const target = parseApiUtcDate(value);
  if (!target) return "Açılış zamanı hazırlanıyor";
  const remaining = target.getTime() - now;
  if (remaining <= 0) return "Çalışmanız açılıyor…";
  if (remaining < 60000) return "Birazdan açılacak";
  const totalMinutes = Math.ceil(remaining / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} gün${hours ? ` ${hours} saat` : ""} kaldı`;
  if (hours > 0) return `${hours} saat${minutes ? ` ${minutes} dakika` : ""} kaldı`;
  return `${minutes} dakika kaldı`;
};

export function NextWorkCountdown({ value, compact = false }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = globalThis.setInterval(() => setNow(Date.now()), 60000);
    return () => globalThis.clearInterval(timer);
  }, []);
  const transition = formatNextWorkTime(value);
  return (
    <section
      className={`stitch-next-work-card${compact ? " stitch-next-work-card--compact" : ""}`}
      aria-label="Sıradaki çalışma zamanı"
    >
      <div className="stitch-next-work-card__meta">
        <span className="stitch-next-work-card__complete">✓ Tamamlandı</span>
        <span className="stitch-next-work-card__eyebrow">Sıradaki çalışma</span>
      </div>
      <h3>Bugünkü çalışmanız tamamlandı</h3>
      <strong className="stitch-next-work-card__date">
        {transition || "Açılış zamanı hazırlanıyor"}
      </strong>
      <p>{transition ? "Sıradaki çalışmanız bu tarihte açılacak." : "Açılış zamanı netleştiğinde burada gösterilecek."}</p>
      <div className="stitch-next-work-card__countdown" aria-live="polite">
        <Clock3 size={18} aria-hidden="true" />
        <strong>{remainingWorkTime(value, now)}</strong>
      </div>
    </section>
  );
}

const statusCopy = {
  awaiting_guide_plan: {
    title: "Yeni çalışmanız rehber tarafından hazırlanıyor",
    body: "Tamamlanan çalışmalarınız korunuyor. Yeni çalışma onaylandığında burada açılacak.",
    icon: Clock3,
  },
  waiting_next_day: {
    title: "Bugünkü çalışmanız tamamlandı",
    body: "Sıradaki çalışmanız İstanbul saatiyle 00:00'dan sonra açılacak.",
    icon: Clock3,
  },
  resting: {
    title: "7 günlük dinlenme sürecine girdiniz",
    body: "Dinlenme süreniz devam ediyor.",
    icon: PauseCircle,
  },
  locked: {
    title: "Deneyim kaydı gerekiyor",
    body: "İlerlemenin açılması için en az 5 karakterlik bir deneyim yazın. Hiçbir şey hissetmediyseniz bunu da yazabilirsiniz.",
    icon: LockKeyhole,
  },
  completed: {
    title: "Programınız tamamlandı",
    body: "Program ekranınız kapanmıştır. Tamamlama bilginiz kaydedildi.",
    icon: CheckCircle2,
  },
  frozen: {
    title: "Programınız geçici olarak durduruldu",
    body: "Program yeniden açıldığında aynı çalışma üzerinden devam edeceksiniz.",
    icon: PauseCircle,
  },
};

const completionError = (requestError) => {
  if (requestError?.response?.data?.detail === "one_completion_per_local_day") {
    return "Bugünkü çalışmanız zaten tamamlandı. Bu çalışmayı İstanbul saatiyle gece 00:00’dan sonra tamamlayabilirsiniz.";
  }
  return formatApiError(requestError);
};

const minuteLabel = (value, fallback) =>
  Number(value) > 0 ? `${Number(value)} dakika` : `${fallback} dakika`;

const intervalLabel = (value) =>
  `yaklaşık ${Number(value) > 0 ? Number(value) : 5} saniyede bir`;

function PracticeGuide({ kind, practice, onOpenGuide }) {
  const primaryLabel = kind === "mantra" ? "mantrayı" : "esmâyı";
  const primaryTitle = kind === "mantra" ? "Mantra uygulaması" : "Esmâ uygulaması";
  const eyesCopy = practice.eyes_closed !== false ? "gözlerinizi kapatın" : "kartta belirtilen göz durumunu uygulayın";
  const guideTarget = kind === "mantra" || kind === "esma" ? "kilavuz-esma" : "kilavuz-meditasyon";
  return (
    <div className="stitch-context-guide" aria-label="Bu çalışmanın kullanım kılavuzu">
      <div className="stitch-context-guide__title">Bu çalışma nasıl uygulanır?</div>
      <ol>
        <li>
          <strong>Hazırlanın.</strong> {practice.environment || "Sessiz ve sakin bir alan"} seçin;
          {" "}{practice.position || "kartta belirtilen pozisyonu"} uygulayın ve {eyesCopy}.
        </li>
        <li>
          <strong>{primaryTitle}.</strong> <span>{practice.name}</span> {primaryLabel}{" "}
          {minuteLabel(practice.vocal_minutes, 20)} boyunca, {intervalLabel(practice.repeat_interval_seconds)}
          {" "}sesli tekrar edin.
        </li>
        <li>
          <strong>Meditasyon kodu.</strong> <span className="font-mono">{practice.meditation_code}</span>{" "}
          kodunu {Number(practice.code_repeat_count) > 0 ? `${practice.code_repeat_count} kez` : "kartta belirtilen sayıda"},
          {" "}{intervalLabel(practice.repeat_interval_seconds)} sesli söyleyin.
        </li>
        <li>
          <strong>Meditasyona geçin.</strong> {practice.meditation_position || "Kartta belirtilen pozisyonda"},
          {" "}{minuteLabel(practice.meditation_minutes, 15)} boyunca gözlerinizi kapatın, bedeninizi serbest bırakın
          ve süreci gözlemleyin.
        </li>
      </ol>
      <p>Süreyi tamamlamanız önerilir; çalışmayı zorlamadan bedeninizin sınırlarını gözetin.</p>
      <button
        type="button"
        onClick={() => onOpenGuide?.(guideTarget)}
        className="stitch-context-guide-link"
      >
        <BookOpen size={16} aria-hidden="true" />
        <span>Ayrıntılı Kullanım Kılavuzunu Aç</span>
        <ChevronDown size={15} aria-hidden="true" className="-rotate-90" />
      </button>
    </div>
  );
}

export default function DailyProgramCard({ program, refresh, onOpenGuide, onCompleted }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [completionConfirming, setCompletionConfirming] = useState(false);
  const completionKeyRef = useRef(machineKey("daily-completion"));

  useEffect(() => {
    completionKeyRef.current = machineKey("daily-completion");
    setGuideOpen(false);
    setCompletionConfirming(false);
  }, [program?.current_step?.id]);

  useEffect(() => {
    if (typeof refresh !== "function" || program?.status === "completed") return undefined;
    const timer = globalThis.setInterval(() => {
      if (globalThis.document?.visibilityState === "visible") {
        Promise.resolve(refresh()).catch(() => {});
      }
    }, 30000);
    return () => globalThis.clearInterval(timer);
  }, [program?.status, refresh]);

  useEffect(() => {
    if (
      typeof refresh !== "function" ||
      program?.status !== "waiting_next_day" ||
      !program?.next_transition_at
    )
      return undefined;
    const transitionAt = parseApiUtcDate(program.next_transition_at);
    if (!transitionAt) return undefined;
    const delay = transitionAt.getTime() - Date.now() + 750;
    if (delay <= 0) {
      Promise.resolve(refresh()).catch(() => {});
      return undefined;
    }
    const timer = globalThis.setTimeout(
      () => Promise.resolve(refresh()).catch(() => {}),
      Math.min(delay, 2147483647),
    );
    return () => globalThis.clearTimeout(timer);
  }, [program?.next_transition_at, program?.status, refresh]);

  if (!program) return null;
  const step = program.current_step;
  const practice = step?.practice;
  const experienceState = program.experience || {};

  const complete = async () => {
    if (!step?.id || saving) return;
    setSaving(true);
    setError("");
    try {
      await api.post(
        `/client-portal/daily-programs/${program.id}/complete`,
        { step_id: step.id, confirmed: true },
        { headers: { "Idempotency-Key": completionKeyRef.current } },
      );
      toast.success("Çalışmanız tamamlandı; deneyim defteriniz açıldı.");
      await refresh?.();
      onCompleted?.();
    } catch (requestError) {
      setError(completionError(requestError));
    } finally {
      setSaving(false);
      setCompletionConfirming(false);
    }
  };

  const defer = async () => {
    if (!step?.id || saving || step.deferred_today) return;
    setSaving(true);
    setError("");
    try {
      await api.post(
        `/client-portal/daily-programs/${program.id}/complete`,
        { step_id: step.id, confirmed: false },
        { headers: { "Idempotency-Key": machineKey("daily-deferred") } },
      );
      toast.success("Aynı çalışma siz yapana kadar açık kalacak.");
      await refresh?.();
    } catch (requestError) {
      setError(completionError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const passive = program.mantra_approval_pending
    ? { title: "Yeni çalışmanız rehber onayında", body: "Mantra çalışmanız onaylandıktan sonra burada açılacak.", icon: Clock3 }
    : statusCopy[program.status];
  const PassiveIcon = passive?.icon || Clock3;

  return (
    <div className="stitch-daily-program">
      {error && (
        <div role="alert" className="stitch-inline-error">
          {error}
        </div>
      )}

      {program.status === "active" && step && practice ? (
        <article className="stitch-daily-active" aria-labelledby="daily-program-title">
          <header className="stitch-daily-header">
            <div className="stitch-daily-progress">
              <span>Bugünün çalışması · {step.ordinal}. çalışma</span>
              <span>2 aşama</span>
            </div>
            <h3
              id="daily-program-title"
              className="stitch-daily-title"
            >
              {step.practice_kind === "mantra" ? "Mantra ve meditasyon" : "Esmâ ve meditasyon"}
            </h3>
            <p className="stitch-daily-supporting">
              Önce {step.practice_kind === "mantra" ? "mantra" : "Esmâ"} çalışmasını, ardından meditasyonu uygulayın. İki adımı bu sırayla tamamlayın.
            </p>
            <button
              type="button"
              className="stitch-context-guide-toggle"
              aria-expanded={guideOpen}
              aria-controls="daily-practice-guide"
              onClick={() => setGuideOpen((current) => !current)}
            >
              <CircleHelp size={16} aria-hidden="true" />
              <span>Nasıl uygulanır?</span>
              <ChevronDown size={16} aria-hidden="true" className={guideOpen ? "rotate-180" : ""} />
            </button>
            {guideOpen && (
              <div id="daily-practice-guide">
                <PracticeGuide
                  kind={step.practice_kind}
                  practice={practice}
                  onOpenGuide={onOpenGuide}
                />
              </div>
            )}
          </header>

          <div className="stitch-daily-steps">
            <section className="stitch-practice-step" aria-labelledby="daily-primary-practice">
              <div
                id="daily-primary-practice"
                className="stitch-practice-toggle"
              >
                <span>
                  <span className="stitch-practice-step-meta">
                    <span>01 · Adım 1 / 2</span>
                    <span>{step.practice_kind === "mantra" ? "Mantra" : "Esmâ"}</span>
                  </span>
                  <span className="stitch-practice-toggle-title">
                    {step.practice_kind === "mantra" ? "Mantra çalışması" : "Esmâ çalışması"}
                  </span>
                </span>
                <CheckCircle2 size={18} aria-hidden="true" />
              </div>
              <div
                id="daily-primary-practice-body"
                className="stitch-practice-body"
              >
                <div className="stitch-practice-value">
                  {practice.name}
                </div>
                {step.practice_kind === "mantra" && practice.catalog_item_id && (
                  <p className="text-sm text-pleiades-muted">
                    Okunuş: {practice.pronunciation || practice.name.toLowerCase()}. Heceleri birbirine bağlayarak okuyun.
                  </p>
                )}
                <dl className="stitch-practice-facts">
                  <div>
                    <dt>Süre</dt>
                    <dd>{minuteLabel(practice.vocal_minutes, 20)}</dd>
                  </div>
                  <div>
                    <dt>Tekrar</dt>
                    <dd>{intervalLabel(practice.repeat_interval_seconds)}</dd>
                  </div>
                  <div>
                    <dt>Pozisyon</dt>
                    <dd>{practice.position || "Oturarak"}</dd>
                  </div>
                  <div>
                    <dt>Ortam</dt>
                    <dd>{practice.environment || "Loş ortam"}{practice.eyes_closed !== false ? " · gözler kapalı" : ""}</dd>
                  </div>
                </dl>
                <p className="stitch-practice-sequence-note">Bu adımı tamamladıktan sonra 2. adıma geçin.</p>
              </div>
            </section>

            <section className="stitch-practice-step" aria-labelledby="daily-meditation">
              <div
                id="daily-meditation"
                className="stitch-practice-toggle"
              >
                <span>
                  <span className="stitch-practice-step-meta">
                    <span>02 · Adım 2 / 2</span>
                    <span>Meditasyon</span>
                  </span>
                  <span className="stitch-practice-toggle-title">
                    {practice.meditation_position || "Ayakta"} meditasyon
                  </span>
                </span>
                <span className="stitch-practice-order-mark" aria-hidden="true">2</span>
              </div>
              <div
                id="daily-meditation-body"
                className="stitch-practice-body"
              >
                <div className="stitch-practice-value stitch-practice-code">
                  <span>{practice.meditation_code}</span>
                </div>
                <p className="stitch-practice-instruction">
                  Kodu {intervalLabel(practice.repeat_interval_seconds)} sesli olarak {practice.code_repeat_count} kez
                  tekrarlayın.
                </p>
                <dl className="stitch-practice-facts">
                  <div>
                    <dt>Süre</dt>
                    <dd>{minuteLabel(practice.meditation_minutes, 15)}</dd>
                  </div>
                  <div>
                    <dt>Pozisyon</dt>
                    <dd>{practice.meditation_position || "Ayakta"}</dd>
                  </div>
                  <div>
                    <dt>Uygulama</dt>
                    <dd>Gözler kapalı, bedeni serbest bırakarak</dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>

          <footer className="stitch-daily-footer">
            <p>
              “Yaptım” kaydından sonra deneyim defteri gece 00:00’a kadar açılır. Yeni çalışma
              ertesi gün gelir.
            </p>
            {completionConfirming ? (
              <div className="stitch-completion-confirm" role="group" aria-label="Çalışma tamamlamayı onayla">
                <strong>Bugünkü çalışmayı tamamladınız mı?</strong>
                <span>Onaydan sonra bu kayıt geri alınamaz.</span>
                <div>
                  <button
                    type="button"
                    onClick={() => setCompletionConfirming(false)}
                    disabled={saving}
                    className="stitch-secondary-button min-h-11"
                  >
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    onClick={complete}
                    disabled={saving}
                    className="stitch-primary-button min-h-11"
                  >
                    {saving ? "Kaydediliyor…" : "Evet, tamamladım"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="stitch-completion-actions" role="group" aria-label="Bugünkü çalışma durumu">
                <button
                  type="button"
                  onClick={defer}
                  disabled={saving || step.deferred_today}
                  className="stitch-secondary-button min-h-12"
                >
                  {step.deferred_today ? "Bugün yapmadım olarak kaydedildi" : "Bugün yapmadım"}
                </button>
                <button
                  type="button"
                  onClick={() => setCompletionConfirming(true)}
                  disabled={saving}
                  className="stitch-primary-button min-h-12"
                >
                  Çalışmayı yaptım
                </button>
              </div>
            )}
          </footer>
        </article>
      ) : passive ? (
        <div className={`stitch-passive-state stitch-passive-${program.status}`} role="status">
          <div className="stitch-passive-icon">
            <PassiveIcon size={program.status === "completed" ? 48 : 26} aria-hidden="true" />
          </div>
          {program.status === "waiting_next_day" && !program.mantra_approval_pending && (
            <NextWorkCountdown value={program.next_transition_at} />
          )}
          {program.status === "resting" && !program.mantra_approval_pending && <span className="stitch-passive-chip">DİNLENME DÖNEMİ</span>}
          {(program.status !== "waiting_next_day" || program.mantra_approval_pending) && (
            <>
              <h3>{passive.title}</h3>
              <p>{passive.body}</p>
            </>
          )}
          {program.status === "resting" && !program.mantra_approval_pending && (
            <>
              <div className="stitch-next-work-notice">
                <span>Sıradaki çalışma</span>
                <strong>{formatNextWorkTime(program.next_transition_at)}</strong>
                <small>
                  {program.next_plan?.rest_days
                    ? `${program.next_plan.rest_days} günlük dinlenme tamamlandığında otomatik açılacak.`
                    : "Dinlenme tamamlandığında otomatik açılacak."}
                </small>
              </div>
              <p className="stitch-passive-note">Dinlenme günleri program süresine dahildir.</p>
            </>
          )}
          {program.status === "completed" && (
            <dl className="stitch-completion-facts">
              <div>
                <dt>Tamamlanan çalışma</dt>
                <dd>{program.completed_steps}</dd>
              </div>
              <div>
                <dt>Durum</dt>
                <dd>Arşivlendi</dd>
              </div>
            </dl>
          )}
        </div>
      ) : null}

      {(experienceState.available || experienceState.submitted_today) && (
        <section className="stitch-experience-form" aria-labelledby="daily-journal-link-title">
          <div className="stitch-experience-heading">
            <div>
              <span>Deneyim defteri</span>
              <strong id="daily-journal-link-title">
                {experienceState.required ? "İlerlemeden önce günlüğünüze yazın" : "Deneyim Günlüğünüz daima açık"}
              </strong>
            </div>
            <small>{experienceState.submitted_today ? "Yeni kayıt ekleyebilirsiniz" : "İsteğe bağlı"}</small>
          </div>
          <p>
            Çalışmanızla ilgili deneyiminizi ayrı Günlük sayfasına yazabilir, kaydettikten sonra
            istediğiniz kadar yeni kayıt ekleyebilirsiniz.
          </p>
          <button
            type="button"
            onClick={onCompleted}
            className="stitch-primary-button min-h-12"
          >
            Deneyim Günlüğüne git
          </button>
        </section>
      )}

    </div>
  );
}
